import { useState, useEffect } from "react";
import { ErrorToast } from "../widgets/AppToast";
import { baseURL, S3_BASE_URL } from "../services/apiConstants";
import Resizer from "react-image-file-resizer";

const useImageUpload = (initialPhotos = []) => {
  const [fileList, setFileList] = useState([]);
  const [base64Images, setBase64Images] = useState([]);

  // Convert File/Blob to Base64 (now with compression)
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      compressImage(file)
        .then((compressedBase64) => resolve(compressedBase64))
        .catch((error) => reject(error));
    });
  };

  // Compress image before converting to Base64 (aggressive settings for testing)
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      Resizer.imageFileResizer(
        file,
        300,
        200,
        "JPEG",
        30,
        0,
        (uri) => {
          resolve(uri);
        },
        "base64",
        (error) => {
          reject(error);
        }
      );
    });
  };

  // Convert URL to Base64
  const urlToBase64 = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch image from ${url}`);
      const blob = await response.blob();
      const compressedBase64 = await compressImage(blob);
      return compressedBase64;
    } catch (error) {
      console.error("Error converting URL to Base64:", error);
      return null;
    }
  };

  // Initialize fileList with API data (edit mode)
  useEffect(() => {
    const initializeFileList = async () => {
      if (initialPhotos && initialPhotos.length > 0) {
        // First, set the fileList with just the URLs so the UI can show them immediately
        const initialFileList = initialPhotos.map((photoPath, index) => {
          const cleanPhotoPath = photoPath.replace(/^\//, "");
          const cleanS3BaseUrl = S3_BASE_URL.replace(/\/$/, "");
          const fullUrl = `${cleanS3BaseUrl}/${cleanPhotoPath}`;
          console.log("Constructed Image URL:", fullUrl);
          
          return {
            uid: `-${index + 1}`,
            name: photoPath.split("/").pop(),
            status: "done",
            url: fullUrl,
          };
        });
        
        setFileList(initialFileList);

        // Then, try to convert to base64 in the background for submission
        const updatedFileList = await Promise.all(
          initialFileList.map(async (file) => {
            const base64 = await urlToBase64(file.url);
            return { ...file, base64 };
          })
        );

        setFileList(updatedFileList);
        setBase64Images(updatedFileList.map((file) => file.base64).filter(Boolean));
      } else {
        setFileList([]);
        setBase64Images([]);
      }
    };
    initializeFileList();
  }, [JSON.stringify(initialPhotos)]);

  // Handle file changes (new uploads)
  const handleFileChange = async ({ fileList: newFileList }) => {
    // Set immediately so UI shows them (status: uploading/ready)
    setFileList(newFileList);

    const updatedFileList = await Promise.all(
      newFileList.map(async (file) => {
        if (file.originFileObj && !file.base64) {
          const base64 = await convertToBase64(file.originFileObj);
          return { ...file, base64 };
        }
        return file;
      })
    );
    setFileList(updatedFileList);
  };

  // Process all files to Base64 (for submission)
  const processFilesToBase64 = async () => {
    if (!fileList.length) {
      return [];
    }

    const base64s = await Promise.all(
      fileList.map(async (file) => {
        if (file.base64) {
          return file.base64;
        } else if (file.originFileObj) {
          return convertToBase64(file.originFileObj);
        } else if (file.url) {
          return urlToBase64(file.url);
        }
        return null;
      })
    );

    const validBase64s = base64s.filter(Boolean);
    setBase64Images(validBase64s);
    return validBase64s;
  };

  return {
    fileList,
    base64Images,
    setFileList,
    handleFileChange,
    processFilesToBase64,
  };
};

export default useImageUpload;
